import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import { sendAdsConversion, ADS_LABELS } from "@/lib/adsConversions";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const PaymentPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [billingType, setBillingType] = useState('azienda');
  const [billing, setBilling] = useState({
    denominazione: '', codice_fiscale: '', partita_iva: '',
    sdi: '', pec: '', indirizzo: '', cap: '', citta: '', provincia: '', email: ''
  });
  const searchParams = new URLSearchParams(window.location.search);
  const isSuccess = searchParams.get('session_id') || searchParams.get('paypal');
  const FORF_SUBS = ["9TX6zCxFKIY", "zrErPRhxSxg"];
  const [addonPortale, setAddonPortale] = useState(false);
  const [renewalTerms, setRenewalTerms] = useState(false);
  // Minimi annui: piano 43 → 301 €; piano Completo 96 → 401 €. La differenza sulle mensilità residue viene addebitata a dicembre.
  const MINIMI_ANNUI = { "9TX6zCxFKIY": { minimo: 301, mensile: 43 }, "zrErPRhxSxg": { minimo: 401, mensile: 96 } };
  const pianoMinimo = MINIMI_ANNUI[slug];
  const mensilitaResidue = 13 - (new Date().getMonth() + 1);
  const conguaglioDicembre = pianoMinimo ? Math.max(0, pianoMinimo.minimo - pianoMinimo.mensile * mensilitaResidue) : 0;

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const res = await axios.get(`${API}/pay/${slug}`);
        setLinkData(res.data);
      } catch (e) {
        setError('Link di pagamento non trovato o non attivo.');
      } finally {
        setLoading(false);
      }
    };
    fetchLink();
  }, [slug]);

  // Google Ads: conversione abbonamento forfettari, una sola volta (controlli lato backend).
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || !FORF_SUBS.includes(slug)) return;
    const flag = `ads_sub_${sessionId}`;
    if (sessionStorage.getItem(flag)) return;
    sessionStorage.setItem(flag, '1');
    (async () => {
      try {
        const res = await axios.post(`${API}/pay/${slug}/ads-conversion`, { session_id: sessionId });
        if (res.data?.send) {
          sendAdsConversion(ADS_LABELS.abbonamentoForfettari, {
            value: res.data.value,
            transaction_id: res.data.transaction_id,
          });
        }
      } catch (e) { /* noop */ }
    })();
  }, [slug]);

  const validateBilling = () => {
    if (!billing.denominazione || !billing.codice_fiscale || !billing.indirizzo || !billing.cap || !billing.citta || !billing.provincia || !billing.email) {
      toast.error('Compila tutti i campi obbligatori');
      return false;
    }
    if (billingType === 'azienda' && !billing.partita_iva) {
      toast.error('Partita IVA obbligatoria per aziende');
      return false;
    }
    if (billingType === 'azienda' && !billing.sdi && !billing.pec) {
      toast.error('Inserire codice SDI oppure PEC');
      return false;
    }
    return true;
  };

  const billingPayload = () => ({
    billing: {
      tipo: billingType,
      denominazione: billing.denominazione,
      codice_fiscale: billing.codice_fiscale,
      partita_iva: billingType === 'azienda' ? (billing.partita_iva || null) : null,
      sdi: billingType === 'azienda' ? (billing.sdi || null) : null,
      pec: billing.pec || null,
      indirizzo: billing.indirizzo,
      cap: billing.cap,
      citta: billing.citta,
      provincia: billing.provincia,
      email: billing.email,
    }
  });

  const handleStripe = async () => {
    if (!validateBilling()) return;
    if (FORF_SUBS.includes(slug) && !renewalTerms) {
      toast.error("Accetta le condizioni di rinnovo del servizio per proseguire");
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/pay/${slug}/stripe`, { ...billingPayload(), addon_portale: addonPortale, renewal_terms_accepted: renewalTerms });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel pagamento');
      setProcessing(false);
    }
  };

  const handlePayPal = async () => {
    if (!validateBilling()) return;
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/pay/${slug}/paypal`, billingPayload());
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore PayPal');
      setProcessing(false);
    }
  };

  const inputClass = "w-full bg-white border border-[#D6CFC0] focus:border-gold rounded px-4 py-3 text-[#0D1527] placeholder:text-[#98A0B0] outline-none transition-colors text-sm";

  if (loading) return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
    </main>
  );

  if (isSuccess) {
    const isForfettariSub = ["9TX6zCxFKIY", "zrErPRhxSxg"].includes(slug);
    return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center p-6" data-testid="payment-success">
      <div className="max-w-md text-center">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="text-green-400" size={40} />
        </div>
        <h1 className="font-heading text-4xl text-[#0D1527] mb-4">PAGAMENTO COMPLETATO</h1>
        {isForfettariSub ? (
          <>
            <p className="text-[#4A5568] mb-8">Grazie! L'abbonamento è attivo. Ultimo passo: compila il modulo con i dati per impostare la tua contabilità.</p>
            <Link to="/forfettari/benvenuto" className="inline-flex items-center gap-2 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105" data-testid="success-to-onboarding">
              COMPILA IL MODULO DI ATTIVAZIONE →
            </Link>
          </>
        ) : (
          <>
            <p className="text-[#4A5568] mb-8">Grazie! Il tuo pagamento e stato ricevuto con successo. Riceverai una conferma via email.</p>
            <Link to="/" className="inline-flex items-center gap-2 text-gold hover:text-yellow-400 transition-colors">
              ← Torna al sito
            </Link>
          </>
        )}
      </div>
    </main>
    );
  }

  if (error) return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <XCircle className="text-netflix mx-auto mb-4" size={48} />
        <h1 className="font-heading text-3xl text-[#0D1527] mb-4">LINK NON TROVATO</h1>
        <p className="text-[#4A5568] mb-8">{error}</p>
        <Link to="/" className="text-gold hover:underline">← Torna al sito</Link>
      </div>
    </main>
  );

  const typeLabel = linkData.payment_type === 'one_time' ? 'Pagamento unico' : 'Abbonamento mensile';

  return (
    <main className="min-h-screen bg-[#F4EFEA] py-10 px-4" data-testid="payment-page">
      <div className="w-full max-w-lg mx-auto">
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="text-[#64708A] hover:text-gold text-sm mb-6 inline-flex items-center gap-2 transition-colors"
          data-testid="payment-back-btn"
        >
          ← TORNA INDIETRO
        </button>
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-gold to-yellow-600 rounded mx-auto mb-4 flex items-center justify-center">
            <span className="font-heading text-black text-2xl font-bold">SC</span>
          </div>
          <p className="text-gold/80 text-xs tracking-widest">SC DOTTORI COMMERCIALISTI</p>
        </div>

        <div className="bg-white border border-[#E8E2D5] rounded-lg overflow-hidden">
          {/* Amount header */}
          <div className="p-6 text-center border-b border-[#E8E2D5]">
            <h1 className="font-heading text-2xl text-[#0D1527] mb-1">{linkData.title}</h1>
            {linkData.description && <p className="text-[#4A5568] text-sm mb-3">{linkData.description}</p>}
            <div className="font-heading text-4xl text-gold">{linkData.amount.toFixed(2)} <span className="text-xl">EUR</span></div>
            <span className="text-xs text-[#64708A]">{typeLabel}</span>
            {linkData.payment_type === 'recurring' && (
              <p className="text-[#8B94A6] text-xs mt-1">Addebito automatico ogni mese</p>
            )}
            {FORF_SUBS.includes(slug) && (
              <div className="mt-5 text-left bg-white border border-gold/25 rounded-lg p-4" data-testid="addon-portale-box">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addonPortale}
                    onChange={(e) => setAddonPortale(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#c8a96e] flex-shrink-0 cursor-pointer"
                    data-testid="addon-portale-checkbox"
                  />
                  <span className="text-[#4A5568] text-xs leading-relaxed">
                    <span className="text-[#0D1527] font-bold">Aggiungi il portale di fatturazione fornito dallo studio — 70 €/anno IVA inclusa</span>
                    <span className="block mt-2">
                      Il portale è lo strumento con cui crei ed emetti le fatture elettroniche: le invia al Sistema
                      di Interscambio (SdI) e le conserva a norma di legge.
                    </span>
                    <span className="block mt-2">
                      <span className="text-[#0D1527]">Il vantaggio:</span> se usi il nostro portale non devi caricare gli XML
                      delle fatture emesse — le vediamo direttamente noi, e tu non devi fare nulla.
                    </span>
                    <span className="block mt-2">
                      Il canone si addebita ora per l'anno in corso e poi automaticamente ogni 1° gennaio sulla carta.
                      A metà dicembre ricevi un'email di preavviso: se preferisci cambiare portale, puoi disattivare
                      il rinnovo rispondendo entro il 31 dicembre.
                    </span>
                    <span className="block mt-2">
                      Non è obbligatorio: sei libero di usare il portale di fatturazione che preferisci — in quel caso
                      carichi gli XML delle fatture emesse su app.casalisilvia.com.
                    </span>
                  </span>
                </label>
                {addonPortale && (
                  <p className="text-gold text-sm font-bold mt-3 pt-3 border-t border-[#E8E2D5]" data-testid="addon-portale-totale">
                    Totale oggi: {(linkData.amount + 70).toFixed(2)} EUR — poi {linkData.amount.toFixed(2)} EUR/mese
                  </p>
                )}
              </div>
            )}
            {FORF_SUBS.includes(slug) && conguaglioDicembre > 0 && (
              <div className="mt-5 text-left bg-white border border-gold/25 rounded-lg p-4" data-testid="conguaglio-box">
                <p className="text-[#4A5568] text-xs leading-relaxed">
                  <span className="text-[#0D1527] font-bold">Importo minimo annuo del servizio: {pianoMinimo?.minimo} €</span>.
                  Attivando questo mese verserai {mensilitaResidue} mensilità entro fine anno
                  ({(pianoMinimo?.mensile * mensilitaResidue).toFixed(0)} €): la differenza di{" "}
                  <span className="text-gold font-bold">{conguaglioDicembre.toFixed(0)} €</span> verrà addebitata
                  automaticamente sulla carta a dicembre.
                </p>
              </div>
            )}
            {FORF_SUBS.includes(slug) && (
              <label className="mt-4 flex items-start gap-3 cursor-pointer text-left" data-testid="renewal-terms-label">
                <input
                  type="checkbox"
                  checked={renewalTerms}
                  onChange={(e) => setRenewalTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#c8a96e] flex-shrink-0 cursor-pointer"
                  data-testid="renewal-terms-checkbox"
                />
                <span className="text-[#4A5568] text-xs leading-relaxed">
                  Accetto le condizioni del servizio: <span className="text-[#0D1527]">al 31/12 l'incarico si rinnova automaticamente per periodi di due anni in due anni</span>, salvo disdetta da comunicare via PEC almeno 3 mesi prima della scadenza. *
                </span>
              </label>
            )}
          </div>

          {/* Billing form */}
          <div className="p-6 space-y-4 border-b border-[#E8E2D5]">
            <h2 className="font-heading text-lg text-[#0D1527] mb-1">DATI DI FATTURAZIONE</h2>

            {/* Privato / Azienda toggle */}
            <div className="flex gap-2" data-testid="billing-type-toggle">
              <button
                type="button"
                onClick={() => setBillingType('privato')}
                className={`flex-1 py-2.5 rounded text-sm font-bold transition-all ${billingType === 'privato' ? 'bg-gold text-black' : 'bg-white text-[#64708A] hover:text-[#0D1527] border border-[#E8E2D5]'}`}
                data-testid="billing-type-privato"
              >
                Privato
              </button>
              <button
                type="button"
                onClick={() => setBillingType('azienda')}
                className={`flex-1 py-2.5 rounded text-sm font-bold transition-all ${billingType === 'azienda' ? 'bg-gold text-black' : 'bg-white text-[#64708A] hover:text-[#0D1527] border border-[#E8E2D5]'}`}
                data-testid="billing-type-azienda"
              >
                Azienda
              </button>
            </div>

            <div>
              <label className="block text-xs text-[#64708A] mb-1">{billingType === 'azienda' ? 'Ragione Sociale *' : 'Nome e Cognome *'}</label>
              <input type="text" required value={billing.denominazione} onChange={(e) => setBilling({...billing, denominazione: e.target.value})} className={inputClass} placeholder={billingType === 'azienda' ? 'Es: Mario Rossi SRL' : 'Es: Mario Rossi'} data-testid="billing-denominazione" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className={billingType === 'privato' ? 'col-span-2' : ''}>
                <label className="block text-xs text-[#64708A] mb-1">Codice Fiscale *</label>
                <input type="text" required value={billing.codice_fiscale} onChange={(e) => setBilling({...billing, codice_fiscale: e.target.value.toUpperCase()})} className={inputClass} placeholder="RSSMRA80A01H501U" data-testid="billing-cf" />
              </div>
              {billingType === 'azienda' && (
                <div>
                  <label className="block text-xs text-[#64708A] mb-1">Partita IVA *</label>
                  <input type="text" value={billing.partita_iva} onChange={(e) => setBilling({...billing, partita_iva: e.target.value})} className={inputClass} placeholder="IT01234567890" data-testid="billing-piva" />
                </div>
              )}
            </div>

            {billingType === 'azienda' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[#64708A] mb-1">Codice SDI</label>
                    <input type="text" value={billing.sdi} onChange={(e) => setBilling({...billing, sdi: e.target.value.toUpperCase()})} className={inputClass} placeholder="0000000" data-testid="billing-sdi" />
                  </div>
                  <div>
                    <label className="block text-xs text-[#64708A] mb-1">PEC</label>
                    <input type="email" value={billing.pec} onChange={(e) => setBilling({...billing, pec: e.target.value})} className={inputClass} placeholder="pec@esempio.it" data-testid="billing-pec" />
                  </div>
                </div>
                <p className="text-[#98A0B0] text-xs">Inserire almeno uno tra Codice SDI e PEC</p>
              </>
            ) : (
              <div>
                <label className="block text-xs text-[#64708A] mb-1">PEC (facoltativa)</label>
                <input type="email" value={billing.pec} onChange={(e) => setBilling({...billing, pec: e.target.value})} className={inputClass} placeholder="pec@esempio.it" data-testid="billing-pec" />
              </div>
            )}

            <div>
              <label className="block text-xs text-[#64708A] mb-1">Indirizzo *</label>
              <input type="text" required value={billing.indirizzo} onChange={(e) => setBilling({...billing, indirizzo: e.target.value})} className={inputClass} placeholder="Via Roma 1" data-testid="billing-indirizzo" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-[#64708A] mb-1">CAP *</label>
                <input type="text" required value={billing.cap} onChange={(e) => setBilling({...billing, cap: e.target.value})} className={inputClass} placeholder="00100" data-testid="billing-cap" />
              </div>
              <div>
                <label className="block text-xs text-[#64708A] mb-1">Citta *</label>
                <input type="text" required value={billing.citta} onChange={(e) => setBilling({...billing, citta: e.target.value})} className={inputClass} placeholder="Roma" data-testid="billing-citta" />
              </div>
              <div>
                <label className="block text-xs text-[#64708A] mb-1">Prov. *</label>
                <input type="text" required maxLength={2} value={billing.provincia} onChange={(e) => setBilling({...billing, provincia: e.target.value.toUpperCase()})} className={inputClass} placeholder="RM" data-testid="billing-provincia" />
              </div>
            </div>

            <div>
              <label className="block text-xs text-[#64708A] mb-1">Email *</label>
              <input type="email" required value={billing.email} onChange={(e) => setBilling({...billing, email: e.target.value})} className={inputClass} placeholder="email@esempio.it" data-testid="billing-email" />
            </div>
          </div>

          {/* Payment buttons */}
          <div className="p-6 space-y-3">
            <button
              onClick={handleStripe}
              disabled={processing}
              className="w-full flex items-center justify-center gap-3 bg-[#635BFF] hover:bg-[#5851db] text-[#0D1527] py-4 rounded-lg font-bold transition-all disabled:opacity-50"
              data-testid="pay-stripe"
            >
              {processing ? 'Reindirizzamento...' : 'Paga con Carta'}
            </button>
            {linkData.payment_type === 'one_time' && (
              <button
                onClick={handlePayPal}
                disabled={processing}
                className="w-full flex items-center justify-center gap-3 bg-[#FFC439] hover:bg-[#f0b820] text-[#003087] py-4 rounded-lg font-bold transition-all disabled:opacity-50"
                data-testid="pay-paypal"
              >
                {processing ? 'Reindirizzamento...' : 'Paga con PayPal'}
              </button>
            )}
            {linkData.payment_type === 'recurring' && (
              <p className="text-center text-[#8B94A6] text-xs">Per gli abbonamenti e disponibile solo il pagamento con carta.</p>
            )}
          </div>
        </div>

        <p className="text-center text-[#98A0B0] text-xs mt-6">
          Pagamento sicuro gestito da Stripe/PayPal. I tuoi dati non vengono salvati sul nostro server.
        </p>
      </div>
    </main>
  );
};

// Scroll to top on route change

export { PaymentPage };
